import { ThemeProps } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/themeSchema';
import { ThemeContract, ThemeProperties } from '@mutiny-pkg/champaign-schemas/src/schemas/themeProperties/types';
import { ThemeConverter } from '@mutiny-pkg/microsite-conversion/src/themeConverter';

const uSpace = (base: string, percent: number) => `${(Number.parseInt(base, 10) * percent) / 100}px`;

const spacingBase = '8';

export function getSpacing(base: string): ThemeContract['spacing'] {
  return {
    '000': '0px',
    '025': uSpace(base, 25),
    '050': uSpace(base, 50),
    '075': uSpace(base, 75),
    '100': uSpace(base, 100),
    '150': uSpace(base, 150),
    '200': uSpace(base, 200),
    '250': uSpace(base, 250),
    '300': uSpace(base, 300),
    '400': uSpace(base, 400),
    '500': uSpace(base, 500),
    '600': uSpace(base, 600),
    '700': uSpace(base, 700),
    '800': uSpace(base, 800),
    '900': uSpace(base, 900),
    '1000': uSpace(base, 1000),
    '1200': uSpace(base, 1200),
    '1600': uSpace(base, 1600),
  };
}

const spacing = getSpacing(spacingBase);

export const color: ThemeContract['color'] = {
  primary: '#4E4C4C',
  secondary: '#F7F6F7',
  tertiary: '#FCFCFB',
  default: '#FFFFFF',
  textDark: '#1E0036',
  textLight: '#FFFFFF',
  none: 'transparent',
  unset: 'unset',
};

const fontFamily = 'Inter';

export const fontWeight: ThemeContract['fontWeight'] = {
  light: '300',
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  variable: '100 900',
};

const typography: ThemeContract['typography'] = {
  body: {
    fontFamily,
    fontSize: '1.125rem',
    fontWeight: fontWeight.regular,
    letterSpacing: '0',
    lineHeight: '1.4',
  },
  h1: {
    fontFamily,
    fontSize: '4.5rem',
    fontWeight: fontWeight.bold,
    letterSpacing: '0',
    lineHeight: '1.2',
  },
  h2: {
    fontFamily,
    fontSize: '3rem',
    fontWeight: fontWeight.bold,
    letterSpacing: '0',
    lineHeight: '1.2',
  },
  h3: {
    fontFamily,
    fontSize: '1.5rem',
    fontWeight: fontWeight.semibold,
    letterSpacing: '0',
    lineHeight: '1.2',
  },
  h4: {
    fontFamily,
    fontSize: '1rem',
    fontWeight: fontWeight.semibold,
    letterSpacing: '0',
    lineHeight: '1.2',
  },
  cta: {
    fontFamily,
    fontSize: '1rem',
    fontWeight: fontWeight.bold,
    letterSpacing: '0',
    lineHeight: '1.2',
  },
  link: {
    fontFamily,
    fontSize: '1.125rem',
    fontWeight: fontWeight.regular,
    letterSpacing: '0',
    lineHeight: '1.2',
    textDecoration: 'underline',
  },
};

export const defaultCTAValues = {
  borderColor: 'none',
  borderRadius: '12px',
  borderStyle: 'none',
  borderWidth: '0',
  letterSpacing: '0',
  paddingBottom: '12px',
  paddingLeft: '20px',
  paddingRight: '20px',
  paddingTop: '12px',
  transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
  hoverBackgroundColor: '#404040',
  fontWeight: fontWeight.bold,
};

const cta: ThemeContract['cta'] = {
  primary: {
    ...defaultCTAValues,
    backgroundColor: color.primary,
    color: color.textLight,
    hoverColor: color.textLight,
  },
  secondary: {
    ...defaultCTAValues,
    backgroundColor: color.secondary,
    color: color.textDark,
    hoverBackgroundColor: color.tertiary,
    hoverColor: color.textDark,
  },
  tertiary: {
    ...defaultCTAValues,
    backgroundColor: color.tertiary,
    color: color.textDark,
    hoverBackgroundColor: color.secondary,
    hoverColor: color.textDark,
  },
};

export const defaultImage: ThemeContract['image'] = {
  borderColor: 'none',
  borderRadius: '0',
  borderStyle: 'none',
  borderWidth: '0',
  boxShadow: 'none',
};

export function defaultCard(tertiaryColor: string): ThemeContract['card'] {
  return {
    alignItems: 'center',
    backgroundColor: tertiaryColor,
    borderColor: 'none',
    borderRadius: '0',
    borderStyle: 'none',
    borderWidth: '0',
    boxShadow: 'none',
  };
}

const fontFaces: ThemeProperties['fontFaces'] = [
  {
    fontFamily,
    fontStyle: 'normal',
    fontWeight: fontWeight.variable,
    src: 'https://fonts.gstatic.com/s/inter/v18/UcCo3FwrK3iLTcviYwYZ8UA3.woff2',
  },
];

export const defaultTheme: ThemeProperties = {
  additionalColors: [],
  card: defaultCard(color.tertiary),
  color,
  cta,
  fontFaces,
  fontWeight,
  image: defaultImage,
  spacing,
  spacingBase,
  typography,
  version: 4,
  logos: {
    light: '',
    dark: '',
  },
};

const themeConverter = new ThemeConverter();
export const newDefaultTheme: ThemeProps = themeConverter.convertThemeProperties(defaultTheme);

export const defaultThemeContract: ThemeContract = {
  card: defaultCard(color.tertiary),
  color,
  cta,
  fontWeight,
  image: defaultImage,
  spacing,
  spacingBase,
  typography,
  logos: {
    light: '',
    dark: '',
  },
};
